import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Card,
  CardContent,
  Chip,
  Tooltip,
  LinearProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as PendingIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { updateCibilScores } from '../../services/studentService';
import {
  getDocumentsByCategory,
  uploadDocuments,
  viewDocument,
  downloadDocument,
  deleteDocument,
} from '../../services/documentService';
import { DOCUMENT_CATEGORIES } from '../../config/constants';
import usePermissions from '../../hooks/usePermissions';
import DocumentPreview from '../documents/DocumentPreview';

const CIBIL_CATEGORY = DOCUMENT_CATEGORIES.CIBIL_REPORTS;

const PERSON_SLOTS = [
  { key: 'student', label: 'Student', slotKey: 'student_cibil' },
  { key: 'father', label: 'Father', slotKey: 'father_cibil' },
  { key: 'mother', label: 'Mother', slotKey: 'mother_cibil' },
];

const CibilReportsSection = ({ studentId, student, onRefresh }) => {
  const { canUploadDocuments } = usePermissions();

  // Scores state
  const [studentScore, setStudentScore] = useState('');
  const [fatherScore, setFatherScore] = useState('');
  const [motherScore, setMotherScore] = useState('');
  const [extras, setExtras] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  // Documents state
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [uploading, setUploading] = useState({});
  const [error, setError] = useState(null);
  const fileInputRefs = useRef({});

  // Preview
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);

  // Initialize scores from student data
  useEffect(() => {
    if (student?.cibilScores) {
      setStudentScore(student.cibilScores.student ?? '');
      setFatherScore(student.cibilScores.father ?? '');
      setMotherScore(student.cibilScores.mother ?? '');
      setExtras(
        (student.cibilScores.extras || []).map((e) => ({
          name: e.name || '',
          score: e.score ?? '',
        }))
      );
    }
  }, [student]);

  // Fetch CIBIL documents
  const fetchDocuments = useCallback(async () => {
    try {
      setLoadingDocs(true);
      const response = await getDocumentsByCategory(studentId, CIBIL_CATEGORY);
      setDocuments(response.data?.data?.documents || []);
    } catch (err) {
      console.error('Error fetching CIBIL documents:', err);
    } finally {
      setLoadingDocs(false);
    }
  }, [studentId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Map documents by slotName
  const docsBySlot = {};
  documents.forEach((doc) => {
    if (doc.slotName) docsBySlot[doc.slotName] = doc;
  });

  // Save scores
  const handleSaveScores = async () => {
    setSaving(true);
    setSaveMsg(null);
    try {
      await updateCibilScores(studentId, {
        studentScore: studentScore === '' ? null : Number(studentScore),
        fatherScore: fatherScore === '' ? null : Number(fatherScore),
        motherScore: motherScore === '' ? null : Number(motherScore),
        extras: extras.map((e) => ({
          name: e.name,
          score: e.score === '' ? null : Number(e.score),
        })),
      });
      setSaveMsg({ type: 'success', text: 'CIBIL scores saved successfully' });
      if (onRefresh) onRefresh();
    } catch (err) {
      setSaveMsg({ type: 'error', text: err.response?.data?.message || 'Failed to save scores' });
    } finally {
      setSaving(false);
    }
  };

  // Add extra person
  const addExtra = () => {
    if (extras.length >= 3) return;
    setExtras((prev) => [...prev, { name: '', score: '' }]);
  };

  // Remove extra person
  const removeExtra = (index) => {
    setExtras((prev) => prev.filter((_, i) => i !== index));
  };

  // Update extra
  const updateExtra = (index, field, value) => {
    setExtras((prev) =>
      prev.map((e, i) => (i === index ? { ...e, [field]: value } : e))
    );
  };

  // Upload handler
  const handleFileSelect = async (slotKey, event) => {
    const file = event.target.files[0];
    if (!file) return;
    setError(null);
    setUploading((prev) => ({ ...prev, [slotKey]: true }));
    try {
      await uploadDocuments(studentId, CIBIL_CATEGORY, [file], slotKey);
      fetchDocuments();
      if (onRefresh) onRefresh();
    } catch (err) {
      setError(`Upload failed: ${err.response?.data?.message || err.message}`);
    } finally {
      setUploading((prev) => ({ ...prev, [slotKey]: false }));
      if (fileInputRefs.current[slotKey]) fileInputRefs.current[slotKey].value = '';
    }
  };

  // View/Download/Delete handlers
  const handleView = async (doc) => {
    try {
      const response = await viewDocument(doc._id);
      const blob = new Blob([response.data], { type: doc.mimeType || 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPreviewBlobUrl(url);
      setPreviewDoc(doc);
    } catch (err) {
      console.error('View failed:', err);
    }
  };

  const handleClosePreview = () => {
    if (previewBlobUrl) window.URL.revokeObjectURL(previewBlobUrl);
    setPreviewDoc(null);
    setPreviewBlobUrl(null);
  };

  const handleDownload = async (doc) => {
    try {
      const response = await downloadDocument(doc._id);
      const blob = new Blob([response.data], { type: doc.mimeType || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.originalName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.originalName}"?`)) return;
    try {
      await deleteDocument(doc._id);
      fetchDocuments();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  // Render a single person row (score + report upload)
  const renderPersonRow = (label, scoreValue, onScoreChange, slotKey) => {
    const doc = docsBySlot[slotKey];
    const isUploading = uploading[slotKey];

    return (
      <Box
        key={slotKey}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 1.5,
          px: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          '&:last-child': { borderBottom: 'none' },
          '&:hover': { backgroundColor: 'action.hover' },
          borderRadius: 1,
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
        }}
      >
        {/* Person label */}
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ minWidth: 80, color: 'text.primary' }}
        >
          {label}
        </Typography>

        {/* Score input */}
        <TextField
          size="small"
          type="number"
          placeholder="Score"
          value={scoreValue}
          onChange={(e) => onScoreChange(e.target.value)}
          inputProps={{ min: 0, max: 900, step: 1 }}
          sx={{ width: 120 }}
        />

        {/* Report upload/status */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, justifyContent: 'flex-end' }}>
          {isUploading ? (
            <Box sx={{ width: 80 }}>
              <LinearProgress size="small" />
            </Box>
          ) : doc ? (
            <>
              <CheckIcon color="success" sx={{ fontSize: 18 }} />
              <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 120 }}>
                {doc.originalName}
              </Typography>
              <Tooltip title="View">
                <IconButton size="small" color="info" onClick={() => handleView(doc)}>
                  <ViewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download">
                <IconButton size="small" onClick={() => handleDownload(doc)}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {canUploadDocuments && (
                <Tooltip title="Replace">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => fileInputRefs.current[slotKey]?.click()}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {canUploadDocuments && (
                <Tooltip title="Delete">
                  <IconButton size="small" color="error" onClick={() => handleDelete(doc)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </>
          ) : canUploadDocuments ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={() => fileInputRefs.current[slotKey]?.click()}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              Upload Report
            </Button>
          ) : (
            <Chip label="No Report" size="small" variant="outlined" color="default" />
          )}

          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            style={{ display: 'none' }}
            ref={(el) => (fileInputRefs.current[slotKey] = el)}
            onChange={(e) => handleFileSelect(slotKey, e)}
          />
        </Box>
      </Box>
    );
  };

  const scoreValues = { student: studentScore, father: fatherScore, mother: motherScore };
  const scoreSetters = {
    student: setStudentScore,
    father: setFatherScore,
    mother: setMotherScore,
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>
          CIBIL Reports
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<SaveIcon />}
          onClick={handleSaveScores}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Scores'}
        </Button>
      </Box>

      {saveMsg && (
        <Alert
          severity={saveMsg.type}
          sx={{ mb: 2 }}
          onClose={() => setSaveMsg(null)}
        >
          {saveMsg.text}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          {/* Main persons: Student, Father, Mother */}
          {PERSON_SLOTS.map((person) =>
            renderPersonRow(
              person.label,
              scoreValues[person.key],
              scoreSetters[person.key],
              person.slotKey
            )
          )}

          {/* Extra persons */}
          {extras.map((extra, index) => (
            <Box
              key={`extra-${index}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                py: 1.5,
                px: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:hover': { backgroundColor: 'action.hover' },
                borderRadius: 1,
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
              }}
            >
              {/* Name input */}
              <TextField
                size="small"
                placeholder="Name"
                value={extra.name}
                onChange={(e) => updateExtra(index, 'name', e.target.value)}
                sx={{ minWidth: 80, width: 140 }}
              />

              {/* Score input */}
              <TextField
                size="small"
                type="number"
                placeholder="Score"
                value={extra.score}
                onChange={(e) => updateExtra(index, 'score', e.target.value)}
                inputProps={{ min: 0, max: 900, step: 1 }}
                sx={{ width: 120 }}
              />

              {/* Report upload for extras */}
              {(() => {
                const slotKey = `extra_cibil_${index + 1}`;
                const doc = docsBySlot[slotKey];
                const isUploading = uploading[slotKey];

                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, justifyContent: 'flex-end' }}>
                    {isUploading ? (
                      <Box sx={{ width: 80 }}>
                        <LinearProgress size="small" />
                      </Box>
                    ) : doc ? (
                      <>
                        <CheckIcon color="success" sx={{ fontSize: 18 }} />
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 100 }}>
                          {doc.originalName}
                        </Typography>
                        <Tooltip title="View">
                          <IconButton size="small" color="info" onClick={() => handleView(doc)}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download">
                          <IconButton size="small" onClick={() => handleDownload(doc)}>
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canUploadDocuments && (
                          <Tooltip title="Replace">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => fileInputRefs.current[slotKey]?.click()}
                            >
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canUploadDocuments && (
                          <Tooltip title="Delete Report">
                            <IconButton size="small" color="error" onClick={() => handleDelete(doc)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    ) : canUploadDocuments ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<UploadIcon />}
                        onClick={() => fileInputRefs.current[slotKey]?.click()}
                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                      >
                        Upload Report
                      </Button>
                    ) : (
                      <Chip label="No Report" size="small" variant="outlined" color="default" />
                    )}

                    <Tooltip title="Remove">
                      <IconButton size="small" color="error" onClick={() => removeExtra(index)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      style={{ display: 'none' }}
                      ref={(el) => (fileInputRefs.current[slotKey] = el)}
                      onChange={(e) => handleFileSelect(slotKey, e)}
                    />
                  </Box>
                );
              })()}
            </Box>
          ))}

          {/* Add Extra button */}
          {extras.length < 3 && (
            <Box sx={{ pt: 1.5, display: 'flex', justifyContent: 'center' }}>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={addExtra}
                sx={{ textTransform: 'none' }}
              >
                Add Extra Person
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Preview popup */}
      <DocumentPreview
        open={!!previewDoc}
        onClose={handleClosePreview}
        blobUrl={previewBlobUrl}
        fileName={previewDoc?.originalName}
        mimeType={previewDoc?.mimeType}
        onDownload={previewDoc ? () => handleDownload(previewDoc) : undefined}
      />
    </Box>
  );
};

export default CibilReportsSection;
